import { useQuery } from '@apollo/client/react';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';

import CollectedModal from './CollectedModal';
import DeliveryItemCard from './DeliveryCollectionCard';
import DeliveryModal from './DeliveryModal';
import DeployModal from './DeployModal';

import DeliveryIcon from '@/assets/DeliveryIcon';
import RequestAccessBlock from '@/components/RequestPermissionTooltip/RequestAccessBlock';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type DeliveryAndCollectionItemState,
  GetDeliveryItemStatesDocument,
  PermissionActionEnum,
  PermissionEntityEnum,
} from '@/graphql/generated/graphql';
import { arrayLength } from '@/utils/helperFunctions';

const DeliveryRegister: React.FC = () => {
  const { selectedReport } = useContext(ReportContext);
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const [deliveryModal, toggleDeliveryModal] = useState<boolean>(false);
  const [deployedModal, toggleDeployedModal] = useState<boolean>(false);
  const [collectedModal, toggleCollectedModal] = useState<boolean>(false);
  const [reportCurrentState, setReportCurrentState] = useState<DeliveryAndCollectionItemState | undefined>(undefined);

  const { data } = useQuery(GetDeliveryItemStatesDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !selectedReport,
    variables: { reportId: selectedReport?.id as string },
  });

  const onItemAction = (action: 'deploy' | 'collect', itemState: DeliveryAndCollectionItemState) => {
    // Setting up for extensability
    if (action === 'deploy') {
      toggleDeployedModal(true);
    }

    if (action === 'collect') {
      toggleCollectedModal(true);
    }

    setReportCurrentState(itemState);
  };

  return (
    <Grid container flexDirection="row" component={Paper} alignContent="flex-start" size={12}>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#E3F1FF',
          paddingX: '16px',
          height: 38,
        }}
        size={12}
      >
        <Typography variant="caption" fontWeight={700}>
          Delivery & Collection Register
        </Typography>
        <Tooltip title="Add Delivery / Collection">
          <IconButton
            onClick={() => toggleDeliveryModal(true)}
            disabled={!canPerformAction(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling)}
          >
            <DeliveryIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      {!canPerformAction(PermissionActionEnum.View, PermissionEntityEnum.Scheduling) ? (
        <RequestAccessBlock
          requiredPermission={{
            action: PermissionActionEnum.View,
            entity: PermissionEntityEnum.Scheduling,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        />
      ) : (
        <Grid
          container
          sx={{
            height: 'calc(100vh - 250px)',
            overflowY: 'scroll',
            marginTop: 1,
          }}
          alignContent="flex-start"
          size={12}
        >
          {arrayLength(data?.getDeliveryItemStates) ? (
            data?.getDeliveryItemStates.map((state, index) => (
              <DeliveryItemCard
                key={`${state.id}-${index}`}
                uuid={state.uuid}
                state={state as DeliveryAndCollectionItemState}
                onItemAction={onItemAction}
              />
            ))
          ) : (
            <Grid container sx={{ p: 1 }} alignItems="flex-start" justifyContent="center" size={12}>
              <Typography>No Delivery Registers Found</Typography>
            </Grid>
          )}
        </Grid>
      )}
      {deliveryModal && <DeliveryModal handleClose={() => toggleDeliveryModal(false)} />}
      {deployedModal && reportCurrentState && (
        <DeployModal
          handleClose={() => {
            toggleDeployedModal(false);
            setReportCurrentState(undefined);
          }}
          stateId={reportCurrentState.id}
        />
      )}
      {collectedModal && reportCurrentState && (
        <CollectedModal
          handleClose={() => {
            toggleCollectedModal(false);
            setReportCurrentState(undefined);
          }}
          stateId={reportCurrentState.id}
        />
      )}
    </Grid>
  );
};

export default DeliveryRegister;
